import { Container, TextField, Button } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { BandBooking } from "../../../__generated__/graphql";
import { DELETE_BOOKING, UPDATE_BOOKING } from "../graphql";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";

import { StagePicker } from "../../../components/StagePicker";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PageContainer } from "../../../components/PageContainer";
import { ButtonContainer } from "../../../components/ButtonContainer";
import { ErrorField } from "../../../components/ErrorField";
import { Info } from "../../../components/Info";
import { UTCDateTimePicker } from "../../../components/UTCDateTimePicker";
import { DurationButtons } from "../../../components/DurationButtons";
import { ErrorRenderer } from "../../../components/ErrorRenderer";
import { useGetDisplayDate } from "../../../components/ZonedDateTimePicker";
import { GET_EDITIONS } from "../../editions/graphql";
import { GET_ANNOUNCEMENT } from "../../announcement/graphql";
import { GET_EDITION } from "../../editionsBands/graphql";

const timeOrNull = yup
  .string()
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .notRequired();

export const schema = yup
  .object({
    selectedStartTime: timeOrNull,
    selectedEndTime: timeOrNull,
  })
  .test("times-consistent", "", function (values) {
    const { selectedStartTime, selectedEndTime } = values || {};

    // both empty → valid
    if (!selectedStartTime && !selectedEndTime) return true;

    // one provided → require the other
    if (selectedStartTime && !selectedEndTime) {
      return this.createError({
        path: "selectedEndTime",
        message: "End time is required when start time is set",
      });
    }
    if (!selectedStartTime && selectedEndTime) {
      return this.createError({
        path: "selectedStartTime",
        message: "Start time is required when end time is set",
      });
    }

    // both provided → enforce ordering
    if (!dayjs(selectedEndTime).isAfter(dayjs(selectedStartTime))) {
      return this.createError({
        path: "selectedEndTime",
        message: "End time must be after start time",
      });
    }

    return true;
  })
  .required();

interface IBookingFormProps {
  booking: BandBooking;
}

export const BookingForm = ({ booking }: IBookingFormProps) => {
  const { refetch } = useQuery(GET_EDITION as any, {
    variables: {
      id: booking.announcement?.editionId!,
    },
    fetchPolicy: "cache-first",
  });

  const navigate = useNavigate();
  const [selectedStartTime, setSelectedStartTime] = useState<Dayjs | null>(
    booking.startTime ? dayjs(booking.startTime) : null
  );
  const [selectedEndTime, setSelectedEndTime] = useState<Dayjs | null>(
    booking.endTime ? dayjs(booking.endTime) : null
  );
  const [selectedStage, setSelectedStage] = useState<string | undefined>(
    booking?.stage?.id
  );
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      selectedStartTime: booking.startTime,
      selectedEndTime: booking.endTime,
    },
  });
  const { getDateInFormat } = useGetDisplayDate();

  const buildRefetchQueries = () => {
    const queries: any[] = [
      { query: GET_EDITIONS },
      { query: GET_ANNOUNCEMENT, variables: { id: booking.announcementId } },
      {
        query: GET_EDITION,
        variables: { id: booking.announcement?.editionId },
      },
      "festivalSingalEdition",
    ];

    if (booking.announcement?.editionId) {
      queries.push({
        query: GET_EDITION,
        variables: { id: booking.announcement.editionId },
      });
    }

    return queries;
  };
  
  const [updateBooking, { loading: updateLoading, error }] = useMutation(
    UPDATE_BOOKING,
    {
      refetchQueries: buildRefetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast("Update complete");
        navigate(-1);
      },
    }
  );

  const [deleteBooking] = useMutation(DELETE_BOOKING, {
    refetchQueries: buildRefetchQueries,
    awaitRefetchQueries: true,
    onCompleted: async () => {
      await refetch()
      toast("booking deleted");
      navigate(-1);
    },
  });

  const updateEndTime = (value: Dayjs | null) => {
    setValue("selectedEndTime", value?.toString() ?? "");
    trigger("selectedEndTime");
    setSelectedEndTime(value);
  };

  const updateStartTime = (value: Dayjs | null) => {
    setValue("selectedStartTime", value?.toString() ?? "");
    trigger("selectedStartTime");
    setSelectedStartTime(value);
    updateEndTime(value);
  };

  // const imageUrl =  ?? ;
  const onSubmit = (data: any) => {
    const date =
      selectedStartTime &&
      new Date(
        Date.UTC(
          selectedStartTime!.year(),
          selectedStartTime!.month(),
          selectedStartTime!.date()
        )
      );

    const isValid = dayjs(selectedStartTime).isValid();

    updateBooking({
      variables: {
        id: booking.id,
        date: isValid ? date?.toISOString() : null,
        startTime: isValid ? selectedStartTime?.toISOString() : null,
        endTime: isValid ? selectedEndTime?.toISOString() : null,
        stageId: selectedStage,
        announcementId: booking.announcementId!,
      },
    });
  };
  const handleDelete = () => {
    confirmAlert({
      title: "Delete this booking?",
      message: "Are you sure? this can't be undone",
      buttons: [
        {
          label: "Yes",
          onClick: () =>
            deleteBooking({ variables: { bookingId: booking.id } }),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  return (
    <PageContainer title={booking.band.name}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container style={{ flexDirection: "column", display: "flex" }}>
          {selectedStartTime ? (
            <h3>{selectedStartTime.format("dddd ll")}</h3>
          ) : (
            <h3>No date selected</h3>
          )}
          <UTCDateTimePicker
            format="YYYY/MM/DD HH:mm"
            label="Start Time"
            ampm={false}
            value={selectedStartTime}
            onChange={(value: Dayjs | null) => updateStartTime(value)}
          />
          <ErrorField>{errors.selectedStartTime?.message}</ErrorField>
          <UTCDateTimePicker
            format="YYYY/MM/DD HH:mm"
            ampm={false}
            label="End Time"
            value={selectedEndTime}
            onChange={(value: Dayjs | null) => updateEndTime(value)}
          />
          <DurationButtons
            startTime={selectedStartTime}
            onSelect={(value) => updateEndTime(value)}
          />
          <ErrorField>{errors.selectedEndTime?.message}</ErrorField>
          <StagePicker
            defaultValue={booking?.stage?.id}
            value={selectedStage}
            handleChange={(value) => setSelectedStage(value)}
            // @ts-ignore
            festivalId={
              booking.edition?.id || booking.announcement?.edition?.id
            }
          />
          <ButtonContainer>
            <Button
              type="submit"
              variant="contained"
              style={{ marginTop: 18, marginBottom: 18 }}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleDelete}
              style={{ backgroundColor: "red" }}
            >
              DELETE
            </Button>
          </ButtonContainer>
        </Container>
        <ErrorRenderer errors={errors} />
      </form>
      <Info />
    </PageContainer>
  );
};
